import  React, { lazy, Suspense, memo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ConfigProvider } from "antd";
import Loading from "components/shared-components/Loading";
import { lightTheme, darkTheme } from "configs/ThemeConfig";
import { resources } from "lang";
import useBodyClass from "utils/hooks/useBodyClass";
import Routes from "routes";
import { supabase } from "auth/SupabaseClient";
import { signOut } from "store/slices/authSlice";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));

const Layouts = () => {
  const token = useSelector((state) => state.auth.token);
  const blankLayout = useSelector((state) => state.theme.blankLayout);
  const dispatch = useDispatch();

  const Layout = token && !blankLayout ? AppLayout : AuthLayout;

  const locale = useSelector((state) => state.theme.locale);

  const direction = useSelector((state) => state.theme.direction);

  const currentTheme = useSelector((state) => state.theme.currentTheme);

  const currentAppLocale = resources[locale];

  useBodyClass(`dir-${direction}`);

  // Check user status on app initialization/refresh
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!token) return;

      try {
        const userId = localStorage.getItem("USER_ID");
        if (!userId) return;

        // Check user status from Users table
        const { data: userData, error: userError } = await supabase
          .from('Users')
          .select('status')
          .eq('id', userId)
          .single();

        if (userError) {
          console.error('Error fetching user status on refresh:', userError);
          // If we can't verify status, sign out for security
          dispatch(signOut());
          return;
        }

        // Check if user status is "InActive"
        if (userData && userData.status === 'InActive') {
          // Sign out the user
          await supabase.auth.signOut();
          dispatch(signOut());
          // Clear all localStorage
          localStorage.removeItem("USER_ID");
          localStorage.removeItem("AUTH_TOKEN");
          localStorage.removeItem("USER_DETAILS");
          localStorage.removeItem("MAP_DETAILS");
          localStorage.removeItem("ORG_DETAILS");
          localStorage.removeItem("LOCKSCREEN");
          localStorage.removeItem("CURRENT_LOCATION");
          // Redirect to login
          window.location.href = '/auth/login';
        }
      } catch (error) {
        console.error('Error checking user status on refresh:', error);
        // On error, sign out for security
        dispatch(signOut());
      }
    };

    checkUserStatus();
  }, [token, dispatch]);

  const themeConfig = currentTheme === "light" ? { ...lightTheme } : { ...darkTheme };

  return (
    <ConfigProvider theme={themeConfig} direction={direction} locale={currentAppLocale.antd}>
      <Suspense fallback={<Loading cover="content" />}>
        <Layout>
          <Routes />
        </Layout>
      </Suspense>
    </ConfigProvider>
  );
};

export default memo(Layouts);
